// Questionnaire de niveau au premier login : 4-5 questions à choix qui règlent les
// axes de difficulté (`user_difficulty_settings`) avant le premier exercice.
// Source de vérité UNIQUE : le frontend affiche ces questions, le backend valide les
// réponses et calcule les réglages avec `computeOnboardingSettings` (même fonction,
// même résultat des deux côtés). Modifiable ensuite dans « Paramètres des Challenges ».
import {
  AlgorithmicComplexity,
  CodeScope,
  CognitiveLoad,
  DifficultySettings,
  LearningMode,
  UnityApiKnowledge
} from '../types/index';

export type OnboardingExperience = 'none' | 'some' | 'regular';
export type OnboardingFundamentals = 'discovering' | 'unsure' | 'comfortable';
export type OnboardingEngine = 'never' | 'tutorial' | 'project';
export type OnboardingInstructions = 'short' | 'detailed';
export type OnboardingGoal = 'certification' | 'fundamentals' | 'own_project';

export interface OnboardingAnswers {
  experience: OnboardingExperience;
  fundamentals: OnboardingFundamentals;
  /** Absent en mode Python/JS (pas de moteur). */
  engine?: OnboardingEngine;
  instructions: OnboardingInstructions;
  goal: OnboardingGoal;
}

export interface OnboardingOption<V extends string = string> {
  value: V;
  label: string;
  hint?: string;
}

export interface OnboardingQuestion {
  id: keyof OnboardingAnswers;
  /** Question affichée ; `{engine}` est remplacé par le nom du moteur du mode courant. */
  prompt: string;
  options: OnboardingOption[];
  /** Modes où la question est posée (défaut : tous). */
  modes?: LearningMode[];
}

const ENGINE_NAMES: Record<LearningMode, string> = {
  [LearningMode.UNITY_CSHARP]: 'Unity',
  [LearningMode.GODOT]: 'Godot',
  [LearningMode.PYTHON_JS]: ''
};

export const ONBOARDING_QUESTIONS: OnboardingQuestion[] = [
  {
    id: 'experience',
    prompt: 'Avez-vous déjà programmé, dans n\'importe quel langage ?',
    options: [
      { value: 'none', label: 'Jamais, je débute', hint: 'C\'est fait pour ça : on part du début.' },
      { value: 'some', label: 'Un peu', hint: 'Quelques scripts, un cours, des tutoriels.' },
      { value: 'regular', label: 'Régulièrement', hint: 'Des projets ou plusieurs mois de pratique.' }
    ]
  },
  {
    id: 'fundamentals',
    prompt: 'Variables, conditions, boucles, fonctions : où en êtes-vous ?',
    options: [
      { value: 'discovering', label: 'Je découvre ces mots' },
      { value: 'unsure', label: 'Je connais, mais j\'hésite encore' },
      { value: 'comfortable', label: 'À l\'aise, je les utilise sans y penser' }
    ]
  },
  {
    id: 'engine',
    prompt: 'Avez-vous déjà utilisé {engine} ?',
    modes: [LearningMode.UNITY_CSHARP, LearningMode.GODOT],
    options: [
      { value: 'never', label: 'Jamais' },
      { value: 'tutorial', label: 'J\'ai ouvert l\'éditeur ou suivi un tutoriel' },
      { value: 'project', label: 'J\'ai réalisé un petit jeu ou un prototype' }
    ]
  },
  {
    id: 'instructions',
    prompt: 'Pour un exercice, vous préférez des consignes…',
    options: [
      { value: 'short', label: 'Courtes : une chose à la fois', hint: 'Recommandé pour commencer.' },
      { value: 'detailed', label: 'Plus riches : plusieurs notions combinées' }
    ]
  },
  {
    id: 'goal',
    prompt: 'Votre objectif principal ?',
    options: [
      { value: 'certification', label: 'Préparer la certification Unity' },
      { value: 'fundamentals', label: 'Acquérir les bases de la programmation' },
      { value: 'own_project', label: 'Avancer sur mon propre projet de jeu' }
    ]
  }
];

/** Questions posées dans un mode donné, moteur nommé. */
export function getOnboardingQuestions(learningMode: LearningMode): OnboardingQuestion[] {
  return ONBOARDING_QUESTIONS
    .filter(q => !q.modes || q.modes.includes(learningMode))
    .map(q => ({ ...q, prompt: q.prompt.replace('{engine}', ENGINE_NAMES[learningMode]) }));
}

/** Vrai si `answers` couvre chaque question du mode avec une valeur autorisée (rien d'autre n'est lu). */
export function isValidOnboardingAnswers(answers: unknown, learningMode: LearningMode): answers is OnboardingAnswers {
  if (!answers || typeof answers !== 'object') return false;
  const record = answers as Record<string, unknown>;
  return getOnboardingQuestions(learningMode).every(q =>
    typeof record[q.id] === 'string' && q.options.some(o => o.value === record[q.id])
  );
}

const EXPERIENCE_RANK: Record<OnboardingExperience, number> = { none: 0, some: 1, regular: 2 };
const FUNDAMENTALS_RANK: Record<OnboardingFundamentals, number> = { discovering: 0, unsure: 1, comfortable: 2 };

/**
 * Réglages de départ déduits des réponses. Volontairement prudents : un exercice trop
 * facile se corrige en deux clics dans les paramètres, un exercice trop dur au premier
 * jour décourage. Les axes ne montent au-dessus de « intermédiaire » (2) que si
 * l'expérience ET les fondamentaux concordent.
 */
export function computeOnboardingSettings(
  answers: OnboardingAnswers,
  learningMode: LearningMode
): Omit<DifficultySettings, 'userId' | 'updatedAt'> {
  const experience = EXPERIENCE_RANK[answers.experience];
  const fundamentals = FUNDAMENTALS_RANK[answers.fundamentals];
  // Niveau effectif = le plus faible des deux : « régulièrement » mais « je découvre » = débutant.
  const level = Math.min(experience, fundamentals);

  const algorithmicComplexity = level === 0
    ? AlgorithmicComplexity.SEQUENTIAL
    : level === 1 ? AlgorithmicComplexity.BASIC : AlgorithmicComplexity.INTERMEDIATE;

  const codeScope = level === 0
    ? CodeScope.ONE_LINE
    : level === 1 ? CodeScope.FUNCTION : CodeScope.COMPONENT;

  // Charge cognitive : « plusieurs notions » seulement pour quelqu'un qui a déjà codé.
  const cognitiveLoad = answers.instructions === 'detailed' && experience > 0
    ? CognitiveLoad.COMBINED
    : CognitiveLoad.ISOLATED;

  // API du moteur : plafonnée à « courant » (2) au départ ; « spécifique » (3) se choisit
  // dans les paramètres. Stocké aussi hors Unity (colonne commune), sans effet en Python/JS.
  const engine = answers.engine ?? 'never';
  const unityApiKnowledge = learningMode === LearningMode.PYTHON_JS || engine === 'never'
    ? UnityApiKnowledge.FUNDAMENTAL
    : engine === 'tutorial' && experience === 0
      ? UnityApiKnowledge.FUNDAMENTAL
      : UnityApiKnowledge.COMMON;

  return { algorithmicComplexity, unityApiKnowledge, codeScope, cognitiveLoad };
}
